import { useRef, useState, type FormEvent } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// The production app (not this static site). CORS is scoped server-side to exactly this
// landing site's origin — see routes/lead-intake.js in the main repo.
const API_BASE = 'https://itsuite.elementalts.cloud';
const CONTACT_ADDRESS = 'hello@nexusrmm.ai';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

type Status = 'idle' | 'loading' | 'sent' | 'error';

export function NotifyForm({ dense = false }: { dense?: boolean }): JSX.Element {
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  // Honeypot: real visitors never see or fill this field; bots that fill every input do.
  const honeypotRef = useRef<HTMLInputElement>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setValidationError(true);
      return;
    }
    setValidationError(false);
    setStatus('loading');
    try {
      const res = await fetch(`${API_BASE}/api/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, company_url: honeypotRef.current?.value || '' }),
      });
      if (!res.ok) throw new Error('request failed');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  }

  if (status === 'sent') {
    return (
      <div className="flex items-center gap-2 text-nexus-silver">
        <CheckCircleIcon fontSize="small" />
        <span className="text-sm">You&apos;re on the design-partner list — we&apos;ll be in touch.</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[460px]">
      <form
        onSubmit={handleSubmit}
        className={`flex w-full gap-3 ${dense ? 'flex-row' : 'flex-col sm:flex-row'}`}
      >
        <TextField
          type="email"
          size="medium"
          placeholder="you@yourcompany.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (validationError) setValidationError(false);
            if (status === 'error') setStatus('idle');
          }}
          error={validationError}
          helperText={validationError ? 'Enter a valid email address' : ' '}
          fullWidth
          disabled={status === 'loading'}
          className="[&_.MuiOutlinedInput-root]:bg-white/[0.04]"
        />
        {/* Honeypot - visually hidden, never display:none (some bots skip those), never seen by a real visitor */}
        <input
          ref={honeypotRef}
          type="text"
          name="company_url"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute -left-[9999px] h-px w-px overflow-hidden opacity-0"
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={status === 'loading'}
          endIcon={status === 'loading' ? undefined : <ArrowForwardIcon />}
          className="h-14 flex-shrink-0 normal-case"
        >
          {status === 'loading' ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : 'Join private preview'}
        </Button>
      </form>
      {status === 'error' && (
        <p className="mt-2 text-xs text-white/50">
          Something went wrong — email us directly at{' '}
          <a href={`mailto:${CONTACT_ADDRESS}`} className="text-nexus-purple-light no-underline hover:underline">
            {CONTACT_ADDRESS}
          </a>{' '}
          instead.
        </p>
      )}
    </div>
  );
}
